import React from 'react';
import { cn } from '../../utils/cn';

/**
 * Skeleton primitives — placeholders animados para estados de carga.
 *
 * Diseño: usan `animate-pulse` + color neutro del tema (bg-muted) para que
 * funcionen en claro/oscuro. La idea es reservar el MISMO layout que el
 * contenido real para que no haya "salto" al terminar de cargar.
 */

// Bloque base. Pasa className para tamaño/forma (w-*, h-*, rounded-*).
export const Skeleton = ({ className, ...props }) => (
  <div
    aria-hidden="true"
    // slate-200 es claramente visible sobre tarjetas blancas (bg-muted #F1F5F9
    // quedaba casi invisible). dark: por si se activa el tema oscuro (darkMode:class).
    className={cn('animate-pulse rounded-md bg-slate-200 dark:bg-slate-700', className)}
    {...props}
  />
);

// Líneas de texto. `lines` = cuántas; la última sale más corta (más natural).
export const SkeletonText = ({ lines = 3, className }) => (
  <div className={cn('space-y-2', className)}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        className={cn('h-4', i === lines - 1 ? 'w-2/3' : 'w-full')}
      />
    ))}
  </div>
);

// Tarjeta de estadística (icono + label + número grande), como las del dashboard.
export const SkeletonStatCard = ({ className }) => (
  <div className={cn('bg-card rounded-lg border border-border p-6', className)}>
    <div className="flex items-center justify-between">
      <div className="space-y-3 flex-1">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-8 w-20" />
      </div>
      <Skeleton className="h-10 w-10 rounded-lg" />
    </div>
  </div>
);

// Fila de N tarjetas de estadística.
export const SkeletonStatCards = ({ count = 4, className }) => (
  <div className={cn('grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4', className)}>
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonStatCard key={i} />
    ))}
  </div>
);

// Tabla: encabezado + `rows` filas de `cols` celdas.
export const SkeletonTable = ({ rows = 5, cols = 4, className }) => (
  <div className={cn('bg-card rounded-lg border border-border overflow-hidden', className)}>
    <div className="border-b border-border p-4">
      <Skeleton className="h-4 w-40" />
    </div>
    <div className="divide-y divide-border">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 p-4">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton
              key={c}
              className={cn('h-4', c === 0 ? 'w-1/4' : 'flex-1')}
            />
          ))}
        </div>
      ))}
    </div>
  </div>
);

// Bloque de gráfica (título + área rectangular).
export const SkeletonChart = ({ className }) => (
  <div className={cn('bg-card rounded-lg border border-border p-6', className)}>
    <Skeleton className="h-4 w-48 mb-6" />
    <Skeleton className="h-64 w-full rounded-lg" />
  </div>
);

// Encabezado de página (título + subtítulo), para el bloque superior.
export const SkeletonPageHeader = ({ className }) => (
  <div className={cn('mb-8 space-y-3', className)}>
    <div className="flex items-center gap-3">
      <Skeleton className="h-10 w-10 rounded-lg" />
      <Skeleton className="h-8 w-64" />
    </div>
    <Skeleton className="h-4 w-96 max-w-full" />
  </div>
);

// Tarjeta de recibo (portal de empleado).
export const SkeletonReceiptCard = ({ className }) => (
  <div className={cn('bg-card rounded-lg border border-border p-4 space-y-3', className)}>
    <div className="flex items-center justify-between">
      <Skeleton className="h-5 w-32" />
      <Skeleton className="h-6 w-16 rounded-full" />
    </div>
    <Skeleton className="h-4 w-40" />
    <div className="flex items-center justify-between pt-2">
      <Skeleton className="h-6 w-24" />
      <Skeleton className="h-8 w-20 rounded-md" />
    </div>
  </div>
);

export default Skeleton;
